import { getCurrentLanguage, getText } from '../translations/translations.js';
import { getCardImagePaths } from '../utils/image-utils.js';
import { openLightbox } from './MarkerPopupCarrouselLightbox.js';
import { getDistanceMeters, formatDistance } from '../utils/location-utils.js';
import { createIconElement, createIconMarkup } from '../utils/icon.js';
import { MARKER_POPUP_ACTION_BUTTONS, CARD_ACTION_BUTTONS, SPOT_CARD_IMAGE_LIMIT } from '/settings.js';
import { showDirectionsPopup } from '../utils/directions.js';
import { openSharePopup } from '../utils/share.js';
import { isFavorite, toggleFavorite } from '../utils/favorites.js';
import { getCardActionsVisibility } from '../cards/cards-hierarchy.js';
import { replaceLinkShortcodes } from '../utils/link-shortcodes.js';

const spotCardImages = new Map();

function getLangSuffix() {
    const lang = getCurrentLanguage();
    if (lang === 'es') return 'es';
    if (lang === 'de') return 'de';
    return 'en';
}

function cleanDescription(text) {
    if (!text) return '';
    return text
        .replace(/\r\n/g, ' ')
        .replace(/\n/g, ' ')
        .replace(/\r/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

function resolveField(spot, baseKey, langSuffix) {
    if (!spot) return '';
    const localized = spot[`${baseKey}_${langSuffix}`];
    return localized || spot[`${baseKey}_en`] || spot[baseKey] || '';
}

function computeDistance(spot) {
    if (typeof window === 'undefined') return '';
    const userLoc = window.userLocation;
    if (!userLoc || !spot?.coordinates) return '';
    const { latitude: userLat, longitude: userLng } = userLoc;
    const { latitude: spotLat, longitude: spotLng } = spot.coordinates;
    if ([userLat, userLng, spotLat, spotLng].some(v => typeof v !== 'number')) return '';
    const meters = getDistanceMeters(userLat, userLng, spotLat, spotLng);
    return formatDistance(meters);
}

async function buildMetadataParts(spot) {
    if (!spot) return [];
    const parts = [];

    // Paid/free entry
    if (spot.is_free === true || spot.is_free === 1) {
        parts.push(await getText('spotInfo.freeEntry'));
    } else if (spot.is_free === false || spot.is_free === 0) {
        parts.push(await getText('spotInfo.paidEntry'));
    }

    // Accessibility
    if (spot.wheelchair === true || spot.wheelchair === 1) {
        parts.push(await getText('spotInfo.wheelchairAccessible'));
    }

    // Pets
    if (spot.petFriendly === true || spot.petFriendly === 1) {
        parts.push(await getText('spotInfo.petFriendly'));
    }

    // Permits
    if (spot.requiresPermit === true || spot.requiresPermit === 1) {
        parts.push(await getText('spotInfo.requiresPermit'));
    }

    // Photography
    if (spot.noPhotography === true || spot.noPhotography === 1) {
        parts.push(await getText('spotInfo.noPhotography'));
    }

    // Closed days (already translated string with numbers)
    if (spot.closedDays) {
        const lang = getCurrentLanguage();
        const dayNumbers = spot.closedDays.toString()
            .split(',')
            .map(d => parseInt(d.trim()))
            .filter(d => !isNaN(d) && d >= 1 && d <= 7);
        if (dayNumbers.length) {
            const labels = [];
            for (const dayNum of dayNumbers) {
                labels.push(await getText(`spotInfo.day${dayNum}`, lang));
            }
            const andText = await getText('spotInfo.and', lang);
            let label = '';
            if (labels.length === 1) {
                label = labels[0];
            } else if (labels.length === 2) {
                label = `${labels[0]} ${andText} ${labels[1]}`;
            } else {
                const last = labels.pop();
                label = `${labels.join(', ')} ${andText} ${last}`;
            }
            const template = await getText('spotInfo.closedDays');
            parts.push(template.replace('{days}', label));
        }
    }

    return parts.filter(Boolean);
}

function mapTags(spot, langSuffix) {
    if (!spot || !Array.isArray(spot.tags)) return [];
    return spot.tags
        .filter(tag => tag && tag.slug !== 'default')
        .map(tag => tag[`name_${langSuffix}`] || tag.name_en || tag.name || tag.slug)
        .filter(Boolean);
}

function registerImages(cardId, images = []) {
    spotCardImages.set(cardId, images);
}

function escapeAttr(value = '') {
    return String(value).replace(/"/g, '&quot;');
}

/**
 * ACTION BUTTONS HELPER - Shared logic for both popup and card modes
 * Builds action button configuration from settings
 */
function buildActionButtons(spot = {}, settings = {}) {
    if (!spot) return { hasFavorite: false, hasDirections: false, hasShare: false };

    const lat = spot.coordinates?.latitude || spot.coords_lat;
    const lng = spot.coordinates?.longitude || spot.coords_lng;
    const title = spot.title || '';

    return {
        spotId: spot.id,
        title,
        lat,
        lng,
        safeTitle: escapeAttr(title),
        hasFavorite: settings.heart && !!spot.id,
        hasDirections: settings.directions && lat != null && lng != null,
        hasShare: settings.share && !!spot.id
    };
}

/**
 * POPUP ACTION BUTTONS FUNCTION - For POPUPS_INFO and POPUPS_IMAGE modes
 * Returns action button configuration based on MARKER_POPUP_ACTION_BUTTONS settings
 */
export function getPopupActionButtons(spot = {}) {
    return buildActionButtons(spot, MARKER_POPUP_ACTION_BUTTONS);
}

/**
 * CARD ACTION BUTTONS FUNCTION - For CARDS mode
 * Returns action button configuration based on CARD_ACTION_BUTTONS settings
 */
export function getCardActionButtons(spot = {}) {
    return buildActionButtons(spot, CARD_ACTION_BUTTONS);
}

/**
 * Legacy alias - kept for backwards compatibility
 */
export function getSpotActionButtons(spot = {}) {
    return getPopupActionButtons(spot);
}

export function openSpotCardLightbox(cardId, initialIndex = 0) {
    const images = spotCardImages.get(cardId) || [];
    if (!images.length) return;
    openLightbox({ images, initialIndex });
}

if (typeof window !== 'undefined') {
    window.__openSpotCardLightbox = openSpotCardLightbox;
}

export async function buildSpotDetailCard(spot) {
    if (!spot) return null;
    const langSuffix = getLangSuffix();
    const title = spot.title || resolveField(spot, 'name', langSuffix);
    const descriptionRaw = resolveField(spot, 'description', langSuffix);
    const description = replaceLinkShortcodes(cleanDescription(descriptionRaw));
    const distance = computeDistance(spot);
    const metadataParts = await buildMetadataParts(spot);
    const meta = [distance, ...metadataParts].filter(Boolean).join(' · ');
    const tags = mapTags(spot, langSuffix);

    const cardId = `spot-${spot.id || 'unknown'}`;
    const filteredImages = (spot.images || []).filter(img => img && img.id);
    registerImages(cardId, filteredImages);

    return {
        id: cardId,
        card_type: 'spot-detail',
        spotId: spot.id,
        title,
        subtitle: spot.city || spot.region || '',
        description,
        meta,
        tags,
        url: spot.url || '',
        isExternal: !!(spot.url && (spot.url.startsWith('http://') || spot.url.startsWith('https://'))),
        images: filteredImages,
        badge: 'info',
        coordinates: spot.coordinates || {
            latitude: spot.coords_lat,
            longitude: spot.coords_lng
        }
    };
}

export function renderSpotImageStrip(card) {
    if (!card || !card.images) return '';
    registerImages(card.id, card.images || []);

    const validImages = (card.images || []).filter(img => img && img.id);
    const totalImages = validImages.length;

    const isMobile = typeof window !== 'undefined'
        ? window.matchMedia('(max-width: 1023px)').matches
        : false;
    const maxImages = isMobile
        ? (SPOT_CARD_IMAGE_LIMIT?.MOBILE ?? 4)
        : (SPOT_CARD_IMAGE_LIMIT?.DESKTOP ?? 3);

    const stripImages = validImages.slice(0, maxImages);
    const extraCount = Math.max(0, totalImages - stripImages.length);

    if (!stripImages.length) return '';

    return `
        <div class="spot-card-image-strip" id="spot-image-strip-${card.spotId || card.id}" data-image-count="${totalImages}">
            ${stripImages.map((img, idx) => {
        const paths = getCardImagePaths(img.id, 'webp');
        const more = idx === stripImages.length - 1 && extraCount > 0
            ? `<span class="spot-card-image-more">+${extraCount}</span>`
            : '';

        const credit = (img.copyright || '').trim();
        const showCopyright = credit && credit !== 'Canary Spots';

        // Copyright overlay - exact same as MarkerPopupCarrousel except hiding "Canary Spots" credit
        let copyrightHtml = '';
        if (showCopyright) {
            const cameraIcon = createIconElement('camera-solid', 'carrousel-copyright-camera');
            if (img.copyright_url) {
                copyrightHtml = `
                    <div class="carrousel-copyright">
                        ${cameraIcon.outerHTML}
                        <a href="${img.copyright_url}" target="_blank" rel="noopener noreferrer" onclick="event.stopPropagation()">${credit}
                        </a>
                    </div>
                `;
            } else {
                copyrightHtml = `
                    <div class="carrousel-copyright">
                        ${cameraIcon.outerHTML}
                        ${credit}
                    </div>
                `;
            }
        }

        return `
                    <button class="spot-card-image" onclick="window.__openSpotCardLightbox('${card.id}', ${idx})" aria-label="View image ${idx + 1}">
                        <img src="${paths.webp}" alt="${img.alt_text || card.title || 'Spot image'}" loading="lazy" />
                        ${copyrightHtml}
                        ${more}
                    </button>
                `;
    }).join('')}
        </div>
    `;
}

export async function renderSpotDetailCard(card) {
    if (!card) return '';
    registerImages(card.id, card.images || []);

    const tagsRow = card.tags && card.tags.length
        ? `<div class="spot-card-tags">${card.tags.map(tag => `<span class="spot-card-tag">#${tag}</span>`).join('')}</div>`
        : '';

    const ctaText = card.url
        ? await getText(card.isExternal ? 'blog.visitWebsite' : 'blog.readMore')
        : '';

    return `
        <div class="spot-card-block" data-card-type="spot-detail" data-spot-id="${card.spotId || ''}">
            <div class="spot-card">
                <div class="spot-card-body">
                    <div class="spot-card-header">
                        <h4 class="spot-card-title">${card.title || ''}</h4>
                        ${card.subtitle ? `<div class="spot-card-subtitle">${card.subtitle}</div>` : ''}
                    </div>
                    ${card.meta ? `<div class="spot-card-meta">${card.meta}</div>` : ''}
                    ${tagsRow}
                    ${card.description ? `<p class="spot-card-description">${card.description}</p>` : ''}
                    ${ctaText && card.url ? `<a class="spot-card-cta" href="${card.url}" ${card.isExternal ? 'target="_blank" rel="noopener noreferrer"' : ''}>${ctaText}</a>` : ''}
                </div>
            </div>
        </div>
    `;
}

export function getSpotDetailCardActions(card) {
    if (!card) return null;

    // Use card action buttons function - uses CARD_ACTION_BUTTONS setting
    return getCardActionButtons({
        id: card.spotId,
        title: card.title,
        coordinates: card.coordinates
    });
}

/**
 * Get action buttons for tag/explore cards (share only)
 */
export function getTagCardActions(cardType = 'explore') {
    const visibility = getCardActionsVisibility(cardType);
    const shareEnabled = CARD_ACTION_BUTTONS?.share === true;

    return {
        hasShare: visibility.share === true && shareEnabled
    };
}

if (typeof window !== 'undefined') {
    window.__spotCardToggleFavorite = async (spotId, btn) => {
        if (!spotId) return;
        await toggleFavorite(spotId);
        const fav = isFavorite(spotId);
        if (btn) btn.classList.toggle('favorited', !!fav);
    };

    window.__spotCardDirections = async (lat, lng) => {
        if (lat == null || lng == null) return;
        // Try to find the correct spot object by lat/lng if spot is not in closure
        let spotForDirections = spot;
        if ((!spotForDirections || !spotForDirections.cid_googleMaps) && window.spots) {
            spotForDirections = window.spots.find(s => {
                return s && s.coordinates &&
                    parseFloat(s.coordinates.latitude) === parseFloat(lat) &&
                    parseFloat(s.coordinates.longitude) === parseFloat(lng);
            }) || spot;
        }
        console.log('[DEBUG][cards directions click] spot:', spotForDirections, 'cid_googleMaps:', spotForDirections && spotForDirections.cid_googleMaps);
        await showDirectionsPopup({ lat, lng, cid_googleMaps: spotForDirections && spotForDirections.cid_googleMaps });
    };

    window.__spotCardShare = (spotId, title) => {
        if (!spotId) return;
        openSharePopup({ spotId, spotTitle: title || '' });
    };

    window.__tagCardShare = async () => {
        const { openSmartSharePopup } = await import('../utils/share.js');
        openSmartSharePopup(window.location.href);
    };
}
